// src/components/chemistry/TestTube.jsx
import { useDrop } from "react-dnd";
import { ItemTypes } from "../../../utils/constants";

export default function TestTube({
  onDrop,
  colorState = "blue",
  reactionComplete,
  fillLevel = 40,
  activeEffect = null,
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.REAGENT,
    drop: (item) => {
      if (!reactionComplete && item && item.id) onDrop(item);
      return {};
    },
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  }));

  const liquidColors = {
    pink: "bg-pink-400",
    green: "bg-green-400",
    brown: "bg-amber-700",
    red: "bg-rose-500",
    orange: "bg-amber-400",
    gray: "bg-gray-400",
    blue: "bg-blue-400",
    yellow: "bg-yellow-300",
  };

  const liquid = liquidColors[colorState] ?? liquidColors.blue;

  return (
    <div className="relative flex justify-center items-end h-72">
      <div
        ref={drop}
        className={[
          "relative flex flex-col justify-end items-center h-64 w-20",
          "border-[6px] border-[#2B3674] rounded-b-full rounded-t-md",
          "bg-[#CFE8FF]/50 shadow-inner overflow-hidden transition-all duration-500",
          isOver ? "ring-4 ring-yellow-400 scale-105" : "",
        ].join(" ")}
      >
        {/* Liquid */}
        <div
          className={[
            "absolute bottom-0 left-0 w-full rounded-t-[40%]",
            liquid,
            "transition-all duration-700",
          ].join(" ")}
          style={{ height: reactionComplete ? "60%" : `${fillLevel}%` }}
        />

        {/* Shine */}
        <div className="absolute left-1 top-0 h-full w-[3px] bg-white/30 rounded-full" />

        {/* 🔥 FIRE / HEAT */}
        {activeEffect === "fire" && !reactionComplete && (
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none">
            <div className="w-8 h-8 bg-gradient-to-t from-orange-600 via-amber-400 to-yellow-200 rounded-full opacity-80 blur-sm animate-pulse" />
            <div className="text-3xl -mt-6 animate-bounce">🔥</div>
          </div>
        )}

        {/* 💨 SMOKE */}
        {activeEffect === "smoke" && !reactionComplete && (
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none">
            <div className="text-3xl animate-bounce">💨</div>
            <div className="flex gap-2">
              <div className="w-4 h-4 bg-white/40 rounded-full blur-sm animate-ping" />
              <div className="w-3 h-3 bg-white/30 rounded-full blur-sm animate-ping delay-150" />
            </div>
          </div>
        )}

        {/* 🫧 BUBBLES */}
        {activeEffect === "bubbles" && !reactionComplete && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none">
            <div className="text-xl animate-bounce">🫧</div>
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-white/80 rounded-full animate-bounce" />
              <div className="w-3 h-3 bg-white/70 rounded-full animate-bounce delay-150" />
            </div>
          </div>
        )}

        {/* DONE */}
        {reactionComplete && (
          <div className="absolute top-1/2 -translate-y-1/2 text-[#2B3674] font-bold animate-pulse text-center text-xs drop-shadow">
            ✅ Reaction Complete!
          </div>
        )}
      </div>
    </div>
  );
}
